// LibrosScreen.jsx
import React, {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
} from "react";
import Papa from "papaparse";
import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import libros from "../data/libros.csv";

const BOOKS = ["Jekyll", "Sanditon", "Cthulhu"];

const clamp = (min, mid, max) => `clamp(${min}, ${mid}, ${max})`;

// AutoFitText: makes the font as large as possible while keeping the whole text visible
const AutoFitText = ({ text, min = 16, max = 140, sx, onFontSize }) => {
    const boxRef = useRef(null);
    const textRef = useRef(null);
    const [fontSize, setFontSize] = useState(max);

    const recompute = useCallback(() => {
        const box = boxRef.current;
        const el = textRef.current;
        if (!box || !el) return;

        const fits = (size) => {
            el.style.fontSize = `${size}px`;
            // scrollHeight/Width reflect overflow; if they exceed the box, it doesn't fit
            return (
                el.scrollHeight <= box.clientHeight &&
                el.scrollWidth <= box.clientWidth
            );
        };

        let lo = min;
        let hi = max;
        let best = min;

        // Binary search for the largest fitting font size
        while (lo <= hi) {
            const mid = Math.floor((lo + hi) / 2);
            if (fits(mid)) {
                best = mid;
                lo = mid + 1;
            } else {
                hi = mid - 1;
            }
        }

        // Apply final size (avoid unnecessary rerenders)
        el.style.fontSize = `${best}px`;
        setFontSize((prev) => (prev === best ? prev : best));

        // Notify parent (used to cap other texts)
        if (typeof onFontSize === "function") onFontSize(best);
    }, [min, max, onFontSize]);

    useLayoutEffect(() => {
        recompute();
    }, [text, recompute]);

    useEffect(() => {
        const box = boxRef.current;
        if (!box) return;

        const ro = new ResizeObserver(() => recompute());
        ro.observe(box);

        return () => ro.disconnect();
    }, [recompute]);

    return (
        <Box
            ref={boxRef}
            sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
            }}
        >
            <Typography
                ref={textRef}
                sx={{
                    fontWeight: 900,
                    fontSize: `${fontSize}px`,
                    lineHeight: 1.05,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    width: "100%",
                    ...sx,
                }}
            >
                {text || "—"}
            </Typography>
        </Box>
    );
};

const BookMemoryPad = ({ csvUrl = libros }) => {
    const [step, setStep] = useState("book"); // "book" | "number" | "result"
    const [selectedBook, setSelectedBook] = useState(null);
    const [typedNumber, setTypedNumber] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [result, setResult] = useState(null); // { memoria, frase, numero }

    // Used to cap Memoria so it never exceeds Frase text size
    const [fraseFontSize, setFraseFontSize] = useState(null);

    const onFraseFontSize = useCallback((s) => {
        setFraseFontSize((prev) => (prev === s ? prev : s));
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadCsv = async () => {
            try {
                setLoading(true);
                setLoadError("");

                const res = await fetch(csvUrl, { cache: "no-store" });
                if (!res.ok)
                    throw new Error(`No se pudo cargar el CSV (${res.status})`);
                const text = await res.text();

                const parsed = Papa.parse(text, {
                    header: true,
                    skipEmptyLines: true,
                });

                if (parsed.errors?.length) {
                    throw new Error(
                        parsed.errors[0]?.message || "Error parseando CSV"
                    );
                }

                const cleaned = (parsed.data || [])
                    .map((r) => {
                        const numRaw = r["#"];
                        const num = Number(String(numRaw ?? "").trim());
                        return {
                            lado: (r["Lado"] ?? "").toString(),
                            numero: Number.isFinite(num) ? num : null,
                            memoria: (r["Memoria"] ?? "").toString(),
                            Jekyll: (r["Jekyll"] ?? "").toString(),
                            Sanditon: (r["Sanditon"] ?? "").toString(),
                            Cthulhu: (r["Cthulhu"] ?? "").toString(),
                        };
                    })
                    .filter((r) => r.numero !== null);

                if (!cancelled) setRows(cleaned);
            } catch (e) {
                if (!cancelled)
                    setLoadError(e?.message || "Error cargando CSV");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadCsv();
        return () => {
            cancelled = true;
        };
    }, [csvUrl]);

    const rowsByNumber = useMemo(() => {
        const m = new Map();
        for (const r of rows) m.set(r.numero, r);
        return m;
    }, [rows]);

    const chooseBook = (book) => {
        setSelectedBook(book);
        setTypedNumber("");
        setResult(null);
        setFraseFontSize(null);
        setStep("number");
    };

    const pressDigit = (d) => {
        setTypedNumber((prev) => {
            const next = (prev + String(d)).replace(/^0+(?=\d)/, "");
            return next.slice(0, 6);
        });
    };

    const correct = () => setTypedNumber((prev) => prev.slice(0, -1));

    const send = () => {
        const n = Number(typedNumber);

        if (!Number.isFinite(n)) {
            setResult({
                numero: typedNumber,
                memoria: "Sin datos",
                frase: "Número inválido",
            });
            setFraseFontSize(null);
            setStep("result");
            return;
        }

        const row = rowsByNumber.get(n);
        const memoria = row?.memoria?.trim() ? row.memoria : "-";
        const fraseRaw = row?.[selectedBook]?.trim?.() ? row[selectedBook] : "";
        const frase = fraseRaw || "-";

        setResult({ numero: n, memoria, frase });
        setFraseFontSize(null);
        setStep("result");
    };

    const resetToNumber = () => {
        setTypedNumber("");
        setResult(null);
        setFraseFontSize(null);
        setStep("number");
    };

    const resetToBooks = () => {
        setSelectedBook(null);
        setTypedNumber("");
        setResult(null);
        setFraseFontSize(null);
        setStep("book");
    };

    const Root = ({ children }) => (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                minHeight: 0, // Let the parent (half-screen) control height on mobile
                display: "flex",
                flexDirection: "column",
                userSelect: "none",
                touchAction: "manipulation",
                overflow: "hidden", // Prevent accidental bleed outside its half
            }}
        >
            {children}
        </Box>
    );

    const TopDisplay = ({ bookLabel, value }) => (
        <Paper
            elevation={0}
            sx={{
                position: "relative",
                borderBottom: "1px solid",
                borderColor: "divider",
                px: 1, // tighter
                py: 0.25, // tighter
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
            }}
        >
            {bookLabel ? (
                <Typography
                    sx={{
                        position: "absolute",
                        top: 4,
                        left: 8,
                        fontWeight: 800,
                        opacity: 0.7,
                        fontSize: clamp("11px", "2vw", "18px"),
                        lineHeight: 1.05,
                    }}
                >
                    {bookLabel}
                </Typography>
            ) : null}

            <Typography
                sx={{
                    fontWeight: 900,
                    letterSpacing: "0.04em",
                    // Half the previous size
                    fontSize: clamp("18px", "5vw", "60px"),
                    lineHeight: 1.05,
                }}
            >
                {value || "—"}
            </Typography>
        </Paper>
    );

    if (loading) {
        return (
            <Root>
                <Box sx={{ p: 0.25 }}>
                    <Typography
                        sx={{
                            opacity: 0.8,
                            fontSize: clamp("14px", "2.2vw", "22px"),
                        }}
                    >
                        Cargando CSV…
                    </Typography>
                </Box>
            </Root>
        );
    }

    if (loadError) {
        return (
            <Root>
                <Box sx={{ p: 0.5 }}>
                    <Typography sx={{ fontWeight: 900, mb: 1 }}>
                        Error
                    </Typography>
                    <Typography sx={{ opacity: 0.85 }}>{loadError}</Typography>
                    <Typography sx={{ mt: 2, opacity: 0.7 }}>
                        Revisa que el CSV tenga:{" "}
                        <b>Lado,#,Memoria,Jekyll,Sanditon,Cthulhu</b>
                    </Typography>
                </Box>
            </Root>
        );
    }

    if (step === "book") {
        return (
            <Root>
                <Stack direction="column" sx={{ flex: 1 }}>
                    {BOOKS.map((b) => (
                        <Button
                            key={b}
                            onClick={() => chooseBook(b)}
                            variant="contained"
                            disableElevation
                            sx={{
                                flex: 1,
                                borderRadius: 0,
                                fontWeight: 900,
                                fontSize: clamp("28px", "6vw", "72px"),
                            }}
                        >
                            {b}
                        </Button>
                    ))}
                </Stack>
            </Root>
        );
    }

    if (step === "number") {
        const keys = [1, 2, 3, 4, 5, 6, 7, 8, 9];

        return (
            <Root>
                <TopDisplay bookLabel={selectedBook} value={typedNumber} />

                <Box
                    sx={{
                        flex: 1,
                        p: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gridTemplateRows: "repeat(4, 1fr)",
                        gap: 0.25, // tighter
                    }}
                >
                    {keys.map((k) => (
                        <Button
                            key={k}
                            onClick={() => pressDigit(k)}
                            variant="contained"
                            disableElevation
                            sx={{
                                height: "100%",
                                fontWeight: 900,
                                fontSize: clamp("26px", "7vw", "90px"),
                            }}
                        >
                            {k}
                        </Button>
                    ))}

                    <Button
                        onClick={correct}
                        variant="outlined"
                        sx={{
                            height: "100%",
                            fontWeight: 900,
                            fontSize: clamp("18px", "3.2vw", "42px"),
                            lineHeight: 1.1,
                            py: 0.25, // tighter
                        }}
                    >
                        Corregir
                    </Button>

                    <Button
                        onClick={() => pressDigit(0)}
                        variant="contained"
                        disableElevation
                        sx={{
                            height: "100%",
                            fontWeight: 900,
                            fontSize: clamp("26px", "7vw", "90px"),
                        }}
                    >
                        0
                    </Button>

                    <Button
                        onClick={send}
                        variant="outlined"
                        sx={{
                            height: "100%",
                            fontWeight: 900,
                            fontSize: clamp("18px", "3.2vw", "42px"),
                            lineHeight: 1.1,
                            py: 0.25, // tighter
                        }}
                    >
                        Enviar
                    </Button>
                </Box>

                <Box sx={{ px: 1, pb: 1 }}>
                    <Button
                        onClick={resetToBooks}
                        fullWidth
                        variant="outlined"
                        sx={{
                            fontWeight: 900,
                            fontSize: clamp("16px", "2.8vw", "34px"),
                            py: 0.25, // tighter
                        }}
                    >
                        Otro libro
                    </Button>
                </Box>
            </Root>
        );
    }

    // step === "result"
    const memoriaText = (result?.memoria ?? "").toString();
    const memoriaTrim = memoriaText.trim();
    const memoriaWords = memoriaTrim
        ? memoriaTrim.split(/\s+/).filter(Boolean)
        : [];
    const memoriaIsShort =
        memoriaTrim && memoriaWords.length <= 2 && memoriaTrim.length <= 16;

    // Give Frase more space (more aggressive than before)
    const memoriaFlex = memoriaIsShort ? 0.6 : 0.85;
    const fraseFlex = memoriaIsShort ? 1.4 : 1.15;

    // Cap Memoria so it never exceeds Frase (dynamic cap)
    const memoriaMax = Math.max(12, Math.floor(fraseFontSize ?? 9999));

    return (
        <Root>
            <TopDisplay
                bookLabel={`${selectedBook} · Nº ${result?.numero ?? "—"}`}
                value={String(result?.numero ?? "")}
            />

            <Stack sx={{ flex: 1, p: 0.25, minHeight: 0 }} spacing={0.5}>
                {/* Panels container */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25, // tighter
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            flex: memoriaFlex,
                            minHeight: 0,
                            border: "1px solid",
                            borderColor: "divider",
                            p: 0.25, // tighter
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 800,
                                opacity: 0.75,
                                mb: 0.25, // tighter
                                // Keep labels modest so content can dominate
                                fontSize: clamp("10px", "1.6vw", "18px"),
                                lineHeight: 1.05,
                            }}
                        >
                            Memoria
                        </Typography>

                        <AutoFitText
                            text={result?.memoria ?? "—"}
                            min={12}
                            max={memoriaMax} // never larger than Frase
                        />
                    </Paper>

                    <Paper
                        elevation={0}
                        sx={{
                            flex: fraseFlex,
                            minHeight: 0,
                            border: "1px solid",
                            borderColor: "divider",
                            p: 0.25, // tighter
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 800,
                                opacity: 0.75,
                                mb: 0.25, // tighter
                                fontSize: clamp("10px", "1.6vw", "18px"),
                                lineHeight: 1.05,
                            }}
                        >
                            Frase
                        </Typography>

                        <AutoFitText
                            text={result?.frase ?? "—"}
                            min={12}
                            max={220} // let it grow more than before
                            onFontSize={onFraseFontSize}
                        />
                    </Paper>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.25, // tighter
                    }}
                >
                    <Button
                        onClick={resetToNumber}
                        variant="outlined"
                        sx={{
                            fontWeight: 900,
                            fontSize: clamp("16px", "2.8vw", "34px"),
                            py: 0.25, // tighter
                        }}
                    >
                        Volver a elegir números
                    </Button>
                    <Button
                        onClick={resetToBooks}
                        variant="outlined"
                        sx={{
                            fontWeight: 900,
                            fontSize: clamp("16px", "2.8vw", "34px"),
                            py: 0.25, // tighter
                        }}
                    >
                        Otro libro
                    </Button>
                </Box>
            </Stack>
        </Root>
    );
};

const LibrosScreen = () => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100vh", // Full viewport; children split it in half on xs
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                overflow: "hidden",
            }}
        >
            <Box
                sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}
            >
                <BookMemoryPad />
            </Box>
            <Divider />
            <Box
                sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden" }}
            >
                <BookMemoryPad />
            </Box>
        </Box>
    );
};

export default LibrosScreen;
